import {
  Button,
  DatePicker,
  FormFeedback,
  FormGroup,
  FormLabel,
  FormRow,
  MultiselectDropdown,
  Textarea,
} from '@opositandopn/ui';
import classnames from 'classnames';
import { Form, Formik, FormikProps } from 'formik';
import { IoSearch } from 'react-icons/io5';
import * as Yup from 'yup';

import { BaseComponentProps } from '../../../../types/base-component.types';
import { FormEntity } from '../../../../types/form.types';
import { SubjectsForm } from '../../../subjects/components/subjects-form/SubjectsForm';
import { Status } from '../../api/useErrorReports';

import styles from './ErrorReportListFilter.module.scss';

export interface ErrorReportListFilterForm {
  fromTime: Date;
  toTime: Date;
  sort: string;
  subject: FormEntity;
  text?: string;
  statuses?: Status[];
}

type ContainerProps = BaseComponentProps & {
  values: ErrorReportListFilterForm;
  onSubmit: (values: ErrorReportListFilterForm) => void;
  onClear: () => void;
};

function ErrorReportListFilter({
  className,
  values,
  onSubmit,
  onClear,
  testId = 'ui-error-report-list-filter',
}: ContainerProps) {
  return (
    <Formik<ErrorReportListFilterForm>
      enableReinitialize
      validationSchema={Yup.object().shape({
        subject: Yup.object().required(),
        text: Yup.string().max(
          2000,
          'El texto no debe superar los 2000 caracteres.',
        ),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);

        onSubmit(values);

        setSubmitting(false);
      }}
      initialValues={{
        fromTime: values.fromTime ?? new Date(),
        toTime: values.toTime ?? new Date(),
        sort: values.sort ?? '-createdAt',
        subject: values.subject ?? {
          id: '',
          name: '',
        },
        text: values.text ?? undefined,
        statuses: values.statuses ?? undefined,
      }}
    >
      {({
        values,
        touched,
        errors,
        isSubmitting,
        handleChange,
        handleSubmit,
      }: FormikProps<ErrorReportListFilterForm>) => (
        <Form
          className={classnames(styles['form'], className)}
          noValidate
          onSubmit={handleSubmit}
          data-testid={testId}
        >
          <FormRow>
            <FormGroup>
              <FormLabel
                info={
                  <>
                    <p>
                      El contenido de este campo puede ser cualquier texto que
                      pueda contener la pregunta.
                    </p>
                    <p>Ejemplos:</p>
                    <ul>
                      <li>Cualquier campo con valor de tipo texto.</li>
                      <li>
                        Ids (de tipo GUID) que pueda contener la pregunta.
                      </li>
                    </ul>
                    <p>
                      Ten en cuenta que estas búsquedas son sensibles a
                      caracteres especiales como las tildes.
                    </p>
                  </>
                }
              >
                Texto libre
              </FormLabel>
              <Textarea
                placeholder="Escribe cualquier texto que pueda contener el simulacro..."
                id="text"
                value={values.text ?? ''}
                onChange={handleChange}
                rows={2}
                error={touched.text && !!errors.text}
              />
              {touched.text && errors.text && (
                <FormFeedback variant="danger">{errors.text}</FormFeedback>
              )}
              <FormFeedback>
                Texto que puede contener el simulacro.
              </FormFeedback>
            </FormGroup>
            <FormGroup>
              <FormLabel
                info={
                  <>
                    <p>
                      El estado del reporte puede ser uno o varios de los
                      siguientes:
                    </p>
                    <ul>
                      <li>Pendiente</li>
                      <li>Cerrado</li>
                      <li>Resuelto</li>
                    </ul>
                  </>
                }
              >
                Estado del reporte
              </FormLabel>
              <MultiselectDropdown
                placeholder="Selecciona uno o varios..."
                onSelect={(values: (string | number)[]) => {
                  handleChange({
                    target: {
                      name: 'statuses',
                      value: values as string[],
                    },
                  });
                }}
                options={[
                  {
                    value: 'open',
                    label: 'Pendiente',
                    selected: values.statuses?.includes('open'),
                  },
                  {
                    value: 'closed',
                    label: 'Cerrado',
                    selected: values.statuses?.includes('closed'),
                  },
                  {
                    value: 'resolved',
                    label: 'Resuelto',
                    selected: values.statuses?.includes('resolved'),
                  },
                ]}
              />
              <FormFeedback></FormFeedback>
            </FormGroup>
            <FormGroup>
              <FormLabel>Fecha de creación</FormLabel>
              <DatePicker
                className={styles['date-picker']}
                startDate={values.fromTime}
                endDate={values.toTime}
                onChange={(fromTime, toTime) => {
                  handleChange({
                    target: {
                      name: 'fromTime',
                      value: fromTime,
                    },
                  });

                  handleChange({
                    target: {
                      name: 'toTime',
                      value: toTime,
                    },
                  });
                }}
                size="lg"
                shortcuts={[
                  'today',
                  'yesterday',
                  'lastSevenDays',
                  'monthToDate',
                  'yearToDate',
                ]}
                locale="es"
              />
              <FormFeedback>Fecha en la que se creó el simulacro.</FormFeedback>
            </FormGroup>
          </FormRow>

          <SubjectsForm unitEnabled={false} lessonEnabled={false} />

          <div className={styles['footer']}>
            <Button
              className={styles['button']}
              variant="outline-primary"
              onClick={onClear}
            >
              Limpiar
            </Button>
            <Button
              className={styles['button']}
              loading={isSubmitting}
              disabled={isSubmitting}
              variant="primary"
              type="submit"
              suffixIcon={<IoSearch />}
            >
              Buscar
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export { ErrorReportListFilter };
